/**
 * Patient AI Health Analysis Page
 * Detailed view with simple language, full trends, remedies, and PDF downloads
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, TrendingUp, Heart, Activity, Shield,
  FileText, Sparkles, AlertTriangle, CheckCircle, Loader2,
  ChevronRight, Calendar, Pill, Leaf, Sun, Moon, Droplets,
  Apple, Dumbbell, Brain, Clock, RefreshCw, Share2, Printer,
  Stethoscope, FlaskConical, UserSearch
} from 'lucide-react';
import HealthForecastChart from '../components/health/HealthForecastChart';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PatientAIAnalysis = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [prakriti, setPrakriti] = useState(null);
  const [trends, setTrends] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [shareModal, setShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchAllHealthData();
  }, []);

  const fetchAllHealthData = async () => {
    setLoading(true);
    const headers = { };

    try {
      // Fetch multiple data sources in parallel
      const [healthRes, prakritiRes, trendsRes] = await Promise.all([
        fetch(`${API_URL}/api/ml-health/status`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/lab-ocr/reports?limit=5`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/wearables/summary?days=30`, { headers }).catch(() => null)
      ]);

      if (healthRes?.ok) {
        const data = await healthRes.json();
        setHealthData(data);
      }

      if (prakritiRes?.ok) {
        const data = await prakritiRes.json();
        setPrakriti(data);
      }

      if (trendsRes?.ok) {
        const data = await trendsRes.json();
        setTrends(data);
      }

    } catch (err) {
      logger.error('Error fetching health data:', err);
    }
    setLoading(false);

    // Generate AI recommendations in background (non-blocking)
    generateRecommendations();
    fetchComprehensiveAnalysis();
  };

  const fetchComprehensiveAnalysis = async () => {
    setAiLoading(true);
    try {
      const user = userStr ? JSON.parse(userStr) : {};
      
      const response = await fetch(`${API_URL}/api/ml-health/comprehensive-analysis`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: 'current', email: user.email || '' })
      });

      if (response.ok) {
        const data = await response.json();
        setAiAnalysis(data);
      }
    } catch (err) {
      logger.error('Comprehensive analysis failed:', err);
    }
    setAiLoading(false);
  };

  const generateRecommendations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ai/health-recommendations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: 'current' })
      });

      if (response.ok) {
        const data = await response.json();
        setRecommendations(data);
      }
    } catch (err) {
      // Use default recommendations
      setRecommendations({
        daily_tips: [
          "Start your day with warm lemon water",
          "Take a 15-minute walk after meals",
          "Practice deep breathing for 5 minutes",
          "Stay hydrated - drink 8 glasses of water"
        ],
        diet: [
          "Include more leafy greens in your meals",
          "Reduce processed food intake",
          "Eat dinner at least 2 hours before bed"
        ],
        lifestyle: [
          "Maintain a consistent sleep schedule",
          "Take breaks every hour if working at desk",
          "Limit screen time before bed"
        ]
      });
    }
  };

  const downloadPDF = async () => {
    setGeneratingPdf(true);
    try {
      const user = userStr ? JSON.parse(userStr) : {};
      
      const response = await fetch(`${API_URL}/api/ml-health/comprehensive-report`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: user.id || 'current',
          email: user.email || '',
          language: 'en'
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `health_report_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
      } else {
        const errText = await response.text();
        logger.error('Report generation failed:', errText);
        alert('Report generation failed. Please try again.');
      }
    } catch (err) {
      logger.error('PDF generation failed:', err);
      alert('Report download failed. Please check your connection and try again.');
    }
    setGeneratingPdf(false);
  };

  const shareAnalysis = async () => {
    setSharing(true);
    setCopied(false);
    try {
      const user = userStr ? JSON.parse(userStr) : {};
      const res = await fetch(`${API_URL}/api/ml-health/share-analysis`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: user.id || 'current', email: user.email || '' })
      });
      if (res.ok) {
        const d = await res.json();
        const origin = window.location.origin;
        setShareUrl(`${origin}/shared-analysis/${d.share_token}`);
        setShareModal(true);
      } else {
        alert('Failed to generate share link. Please try again.');
      }
    } catch {
      alert('Network error. Please try again.');
    }
    setSharing(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const sections = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'vitals', label: 'My Vitals', icon: Heart },
    { id: 'trends', label: 'Health Trends', icon: TrendingUp },
    { id: 'allopathic', label: 'Allopathic', icon: Stethoscope },
    { id: 'ayurvedic', label: 'Ayurvedic', icon: Leaf },
    { id: 'tests', label: 'Suggested Tests', icon: FlaskConical },
    { id: 'lifestyle', label: 'Lifestyle', icon: Sun },
    { id: 'doctor', label: 'Find Doctor', icon: UserSearch }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Analyzing your health data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50" data-testid="patient-analysis-page">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-full transition"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-orange-500" />
                  AI Health Analysis
                </h1>
                <p className="text-sm text-gray-500">Your personalized health insights</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchAllHealthData}
                className="p-2 text-gray-500 hover:bg-orange-50 rounded-lg transition"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={shareAnalysis}
                disabled={sharing || !aiAnalysis}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50 disabled:opacity-50 transition"
                data-testid="share-analysis-btn"
              >
                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                Share
              </button>
              <button
                onClick={downloadPDF}
                disabled={generatingPdf}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 transition shadow-md"
                data-testid="download-pdf-btn"
              >
                {generatingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Download Report
              </button>
            </div>
          </div>

          {/* Section Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2" data-testid="section-tabs">
            {sections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                data-testid={`tab-${section.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition ${
                  activeSection === section.id
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-orange-50'
                }`}
              >
                <section.icon className="w-4 h-4" />
                {section.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Overview Section */}
        {activeSection === 'overview' && (
          <>
            {/* Health Score Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-gray-900">Your Health Summary</h2>
                <span className="text-sm text-gray-500">Updated today</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-orange-50 to-amber-100 rounded-xl p-4 text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl font-bold text-white">{aiAnalysis?.health_score ?? '—'}</span>
                  </div>
                  <p className="font-medium text-gray-900">Health Score</p>
                  <p className="text-sm text-orange-600">{aiAnalysis?.overall_status ?? 'Analyzing...'}</p>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 text-center">
                  <Heart className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{aiAnalysis?.vitals?.heart_rate || '—'}</p>
                  <p className="text-sm text-gray-600">Heart Rate (bpm)</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-yellow-100 rounded-xl p-4 text-center">
                  <Moon className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{aiAnalysis?.vitals?.spo2 || '—'}%</p>
                  <p className="text-sm text-gray-600">SpO2</p>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 text-center">
                  <Activity className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{aiAnalysis?.vitals?.blood_pressure || '—'}</p>
                  <p className="text-sm text-gray-600">Blood Pressure</p>
                </div>
              </div>

              {/* Simple Language Summary */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Brain className="w-5 h-5 text-orange-500" />
                  What This Means For You
                </h3>
                {aiAnalysis ? (
                  <div className="text-gray-700 leading-relaxed">
                    <p className="mb-2"><strong>Status:</strong> {aiAnalysis.overall_status} (Score: {aiAnalysis.health_score}/100)</p>
                    {aiAnalysis.key_findings?.length > 0 && (
                      <ul className="list-disc pl-5 space-y-1 text-sm">
                        {aiAnalysis.key_findings.map((f, i) => <li key={`finding-${i}-${f.slice(0,20)}`}>{f}</li>)}
                      </ul>
                    )}
                  </div>
                ) : aiLoading ? (
                  <p className="text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> AI is analyzing your complete health data...</p>
                ) : (
                  <p className="text-gray-700 leading-relaxed">
                    Your overall health is looking good. Upload lab reports and connect wearables for a more precise AI-powered analysis.
                  </p>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => navigate('/lab-reports')}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition text-left group border-2 border-transparent hover:border-orange-300"
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-100 to-amber-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-orange-600" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition" />
                </div>
                <h3 className="font-semibold text-gray-900 mt-4">Upload Lab Report</h3>
                <p className="text-sm text-gray-500">Get AI analysis of your reports</p>
              </button>

              <button
                onClick={() => navigate('/prakriti-assessment')}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition text-left group border-2 border-transparent hover:border-amber-300"
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-100 to-yellow-100 rounded-xl flex items-center justify-center">
                    <Leaf className="w-6 h-6 text-amber-600" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-amber-500 transition" />
                </div>
                <h3 className="font-semibold text-gray-900 mt-4">Prakriti Assessment</h3>
                <p className="text-sm text-gray-500">Discover your Ayurvedic constitution</p>
              </button>

              <button
                onClick={() => navigate('/health-analysis')}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition text-left group border-2 border-transparent hover:border-orange-300"
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-100 to-amber-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-orange-600" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition" />
                </div>
                <h3 className="font-semibold text-gray-900 mt-4">Full Analysis</h3>
                <p className="text-sm text-gray-500">View comprehensive health trends</p>
              </button>
            </div>
          </>
        )}

        {/* Vitals Section */}
        {activeSection === 'vitals' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Your Vital Signs</h2>
            {aiLoading && !aiAnalysis ? (
              <div className="flex items-center gap-2 text-gray-500 py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading vitals from AI analysis...</div>
            ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: 'Heart Rate', value: aiAnalysis?.vitals?.heart_rate || '—', unit: 'bpm', icon: Heart, color: 'red' },
                { name: 'Blood Pressure', value: aiAnalysis?.vitals?.blood_pressure || '—', unit: 'mmHg', icon: Activity, color: 'blue' },
                { name: 'SpO2', value: aiAnalysis?.vitals?.spo2 || '—', unit: '%', icon: Droplets, color: 'cyan' },
                { name: 'Temperature', value: aiAnalysis?.vitals?.temperature || '—', unit: '°F', icon: Sun, color: 'orange' },
              ].map((vital, i) => (
                <div key={`vital-${vital.name}`} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <vital.icon className="w-5 h-5 text-gray-600" />
                    <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">recorded</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{vital.value}</p>
                  <p className="text-sm text-gray-600">{vital.name} ({vital.unit})</p>
                </div>
              ))}
            </div>

            {aiAnalysis?.data_sources && (
              <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                <p className="text-blue-800">
                  <strong>Data Sources:</strong> {aiAnalysis.data_sources.wearable_readings} wearable readings, {aiAnalysis.data_sources.lab_reports} lab reports, {aiAnalysis.data_sources.prescriptions} prescriptions, {aiAnalysis.data_sources.vital_records} vital records analyzed.
                </p>
              </div>
            )}
            </>
            )}
          </div>
        )}

        {/* Trends Section - Live Prophet/ARIMA/Ensemble Forecasting */}
        {activeSection === 'trends' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">AI Health Forecasting</h2>
              <p className="text-sm text-gray-500 mb-4">
                Advanced predictions using Prophet, ARIMA, and ensemble models with 95% confidence intervals.
              </p>
              <HealthForecastChart />
            </div>
          </div>
        )}

        {/* Allopathic Section */}
        {activeSection === 'allopathic' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Allopathic (Modern Medicine) Analysis</h2>
                  <p className="text-sm text-gray-500">Evidence-based medical recommendations from your data</p>
                </div>
              </div>
              
              {aiLoading && !aiAnalysis ? (
                <div className="flex items-center gap-2 text-gray-500 py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> GPT-4o is analyzing your health data...</div>
              ) : (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-3">Recommendations</h3>
                  <ul className="space-y-3 text-gray-700">
                    {(aiAnalysis?.allopathic_recommendations || []).map((rec, i) => (
                      <li key={`allo-rec-${i}`} className="flex gap-2"><CheckCircle className="w-4 h-4 text-blue-500 mt-1 flex-shrink-0" /> {rec}</li>
                    ))}
                    {(!aiAnalysis?.allopathic_recommendations || aiAnalysis.allopathic_recommendations.length === 0) && (
                      <li className="text-gray-500 italic">Upload lab reports to get personalized recommendations.</li>
                    )}
                  </ul>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Corrective Measures
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    {(aiAnalysis?.corrective_measures || []).map((measure, i) => (
                      <li key={`corr-${i}`} className="flex gap-2"><span className="text-amber-500 font-bold">{i + 1}.</span> {measure}</li>
                    ))}
                    {(!aiAnalysis?.corrective_measures || aiAnalysis.corrective_measures.length === 0) && (
                      <li className="text-gray-500 italic">No corrective measures identified at this time.</li>
                    )}
                  </ul>
                </div>

                {aiAnalysis?.risk_assessment && (
                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-3">Risk Assessment</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {Object.entries(aiAnalysis.risk_assessment).map(([key, val]) => (
                        <div key={key} className="text-center p-3 rounded-lg bg-white border">
                          <p className="text-sm text-gray-500 capitalize">{key}</p>
                          <p className={`text-lg font-bold ${val.level === 'low' ? 'text-green-600' : val.level === 'moderate' ? 'text-amber-600' : 'text-red-600'}`}>
                            {val.level?.toUpperCase()}
                          </p>
                          <p className="text-xs text-gray-400">Score: {val.score}/100</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>
        )}

        {/* Ayurvedic Section */}
        {activeSection === 'ayurvedic' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Leaf className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Ayurvedic System Analysis</h2>
                  <p className="text-sm text-gray-500">Traditional Indian medicine — personalized to your Prakriti</p>
                </div>
              </div>
              
              {aiLoading && !aiAnalysis ? (
                <div className="flex items-center gap-2 text-gray-500 py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> GPT-4o is assessing your Ayurvedic profile...</div>
              ) : (
              <div className="space-y-4">
                {aiAnalysis?.prakriti && (
                <div className="p-4 bg-green-50 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-2">Prakriti Assessment</h3>
                  <p className="text-gray-700 mb-3">
                    Dominant Dosha: <strong>{aiAnalysis.prakriti.dominant_dosha || 'Vata-Pitta'}</strong>.{' '}
                    {aiAnalysis.prakriti.assessment || 'A detailed Prakriti assessment with a qualified Ayurvedic physician is recommended for precise dosha balancing.'}
                  </p>
                  <button onClick={() => navigate('/prakriti-assessment')} className="text-sm text-green-700 font-medium hover:underline">
                    Take Full Prakriti Assessment &rarr;
                  </button>
                </div>
                )}

                <div className="p-4 bg-emerald-50 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-3">Ayurvedic Remedies & Recommendations</h3>
                  <ul className="space-y-3 text-gray-700">
                    {(aiAnalysis?.ayurvedic_recommendations || []).map((rec, i) => (
                      <li key={`ayur-rec-${i}`} className="flex gap-2"><Leaf className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> {rec}</li>
                    ))}
                    {(!aiAnalysis?.ayurvedic_recommendations || aiAnalysis.ayurvedic_recommendations.length === 0) && (
                      <li className="text-gray-500 italic">Complete a Prakriti assessment for personalized Ayurvedic recommendations.</li>
                    )}
                  </ul>
                </div>

                {aiAnalysis?.ayurvedic_doctor_referral?.recommended && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <UserSearch className="w-4 h-4 text-amber-600" />
                    Ayurvedic Doctor Referral
                  </h3>
                  <p className="text-gray-700 text-sm mb-2"><strong>Recommended:</strong> {aiAnalysis.ayurvedic_doctor_referral.reason}</p>
                  <p className="text-gray-700 text-sm"><strong>Specialization:</strong> {aiAnalysis.ayurvedic_doctor_referral.specialization}</p>
                  <button onClick={() => setActiveSection('doctor')} className="mt-3 text-sm text-amber-700 font-medium hover:underline">
                    Find a Doctor &rarr;
                  </button>
                </div>
                )}
              </div>
              )}
            </div>
          </div>
        )}

        {/* Suggested Tests Section */}
        {activeSection === 'tests' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <FlaskConical className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Suggested Diagnostic Tests</h2>
                <p className="text-sm text-gray-500">AI-recommended based on your health data</p>
              </div>
            </div>
            
            {aiLoading && !aiAnalysis ? (
              <div className="flex items-center gap-2 text-gray-500 py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Analyzing which tests you need...</div>
            ) : (
            <>
            <div className="space-y-4">
              {(aiAnalysis?.suggested_tests || []).map((test, i) => {
                const priority = i < 2 ? 'High' : i < 4 ? 'Medium' : 'Routine';
                const borderColor = priority === 'High' ? '#ef4444' : priority === 'Medium' ? '#f59e0b' : '#3b82f6';
                return (
                  <div key={`test-${i}`} className="p-4 rounded-xl border-l-4 bg-gray-50" style={{borderColor}}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{typeof test === 'string' ? test : test.name}</h3>
                        {typeof test !== 'string' && test.reason && <p className="text-sm text-gray-600 mt-1">{test.reason}</p>}
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                        priority === 'High' ? 'bg-red-100 text-red-700' : 
                        priority === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {priority}
                      </span>
                    </div>
                  </div>
                );
              })}
              {(!aiAnalysis?.suggested_tests || aiAnalysis.suggested_tests.length === 0) && (
                <p className="text-gray-500 italic text-center py-4">Upload lab reports for AI-recommended tests.</p>
              )}
            </div>

            <div className="mt-6 p-4 bg-indigo-50 rounded-xl">
              <p className="text-indigo-800 text-sm">
                <strong>Note:</strong> These tests are AI-recommended based on your health data. 
                Please consult your doctor before ordering any tests. Your physician may adjust this list based on your complete medical history.
              </p>
            </div>
            </>
            )}
          </div>
        )}

        {/* Find Ayurvedic Doctor Section */}
        {activeSection === 'doctor' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <UserSearch className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Find a Qualified Ayurvedic Doctor</h2>
                  <p className="text-sm text-gray-500">
                    {aiAnalysis?.ayurvedic_doctor_referral?.reason || 'Based on your analysis, a consultation with an Ayurvedic physician is recommended'}
                  </p>
                </div>
              </div>
              
              {aiAnalysis?.ayurvedic_doctor_referral && (
              <div className="p-4 bg-teal-50 rounded-xl border border-teal-200 mb-6">
                <h3 className="font-semibold text-teal-900 mb-2">AI Recommendation</h3>
                <p className="text-gray-700 text-sm mb-1">
                  <strong>Consultation Recommended:</strong> {aiAnalysis.ayurvedic_doctor_referral.recommended ? 'Yes' : 'Optional'}
                </p>
                <p className="text-gray-700 text-sm">
                  <strong>Suggested Specialization:</strong> {aiAnalysis.ayurvedic_doctor_referral.specialization || 'Kayachikitsa (Internal Medicine)'}
                </p>
              </div>
              )}

              <h3 className="font-semibold text-gray-900 mb-4">Recommended Specializations</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {[
                  { spec: 'Kayachikitsa', desc: 'Internal Medicine — for systemic health issues, digestive disorders, metabolic conditions', icon: Stethoscope },
                  { spec: 'Panchakarma Specialist', desc: 'Detoxification therapies — Virechana, Basti, Nasya for deep cleansing', icon: Droplets },
                  { spec: 'Rasayana Specialist', desc: 'Rejuvenation therapy — anti-aging, immunity building, Ojas enhancement', icon: Sparkles },
                  { spec: 'Dravyaguna Expert', desc: 'Herbal pharmacology — custom herbal formulations for your Prakriti', icon: Leaf },
                ].map((s, i) => (
                  <div key={`spec-${s.spec}`} className="p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-teal-300 transition">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <s.icon className="w-4 h-4 text-teal-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{s.spec}</h4>
                        <p className="text-sm text-gray-600 mt-1">{s.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-gradient-to-r from-teal-50 to-green-50 rounded-xl border border-teal-200">
                <h3 className="font-semibold text-gray-900 mb-3">How to Find a Verified Ayurvedic Doctor</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" /> Look for BAMS (Bachelor of Ayurvedic Medicine and Surgery) or MD (Ayurveda) qualification</li>
                  <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" /> Check registration with the Central Council of Indian Medicine (CCIM) or state Ayurveda board</li>
                  <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" /> Prefer practitioners affiliated with recognized Ayurveda hospitals or universities</li>
                  <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" /> Ask about their experience with your specific health concerns</li>
                </ul>
              </div>

              <button
                onClick={() => navigate('/appointments')}
                className="w-full mt-6 py-4 bg-gradient-to-r from-teal-600 to-green-600 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-green-700 transition flex items-center justify-center gap-2"
                data-testid="book-ayurvedic-consultation"
              >
                <Calendar className="w-5 h-5" />
                Book an Ayurvedic Consultation
              </button>
            </div>
          </div>
        )}


        {/* Lifestyle Tips Section */}
        {activeSection === 'lifestyle' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">AI Lifestyle Recommendations</h2>
              
              {aiLoading && !aiAnalysis ? (
                <div className="flex items-center gap-2 text-gray-500 py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Generating lifestyle tips...</div>
              ) : (
              <>
              {/* Dynamic AI lifestyle tips */}
              {aiAnalysis?.lifestyle_tips && aiAnalysis.lifestyle_tips.length > 0 && (
                <div className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-100">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-orange-500" />
                    Personalized for You
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    {aiAnalysis.lifestyle_tips.map((tip, i) => (
                      <li key={`lifestyle-tip-${i}`} className="flex gap-2">
                        <CheckCircle className="w-4 h-4 text-orange-500 mt-1 flex-shrink-0" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <h3 className="font-semibold text-gray-900 mb-3">Daily Routine</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-orange-200 rounded-xl p-4 bg-orange-50">
                  <div className="flex items-center gap-2 mb-3">
                    <Sun className="w-5 h-5 text-orange-500" />
                    <h3 className="font-semibold text-gray-900">Morning (6-9 AM)</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>&#8226; Wake up before sunrise</li>
                    <li>&#8226; Drink warm water</li>
                    <li>&#8226; 15 min yoga or stretching</li>
                    <li>&#8226; Light, nutritious breakfast</li>
                  </ul>
                </div>
                <div className="border border-yellow-200 rounded-xl p-4 bg-yellow-50">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-5 h-5 text-yellow-600" />
                    <h3 className="font-semibold text-gray-900">Afternoon (12-3 PM)</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>&#8226; Main meal at lunchtime</li>
                    <li>&#8226; Short walk after eating</li>
                    <li>&#8226; Stay hydrated</li>
                    <li>&#8226; Take breaks from screen</li>
                  </ul>
                </div>
                <div className="border border-purple-200 rounded-xl p-4 bg-purple-50">
                  <div className="flex items-center gap-2 mb-3">
                    <Moon className="w-5 h-5 text-purple-500" />
                    <h3 className="font-semibold text-gray-900">Evening (6-9 PM)</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>&#8226; Light dinner before 7 PM</li>
                    <li>&#8226; Evening walk</li>
                    <li>&#8226; Reduce screen time</li>
                    <li>&#8226; Relaxation activities</li>
                  </ul>
                </div>
                <div className="border border-indigo-200 rounded-xl p-4 bg-indigo-50">
                  <div className="flex items-center gap-2 mb-3">
                    <Moon className="w-5 h-5 text-indigo-500" />
                    <h3 className="font-semibold text-gray-900">Night (9-10 PM)</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>&#8226; Warm milk with turmeric</li>
                    <li>&#8226; 10 min meditation</li>
                    <li>&#8226; Sleep by 10 PM</li>
                    <li>&#8226; 7-8 hours of sleep</li>
                  </ul>
                </div>
              </div>
              </>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Share Modal */}
      {shareModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="share-modal-overlay" onClick={() => setShareModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()} data-testid="share-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Share2 className="w-5 h-5 text-orange-500" /> Share Analysis</h2>
              <button onClick={() => setShareModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Anyone with this link can view your AI health analysis. The link is valid for <strong>6 months</strong>.
            </p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 px-3 py-2 border rounded-lg text-sm bg-gray-50 text-gray-700 select-all"
                data-testid="share-url-input"
                onClick={e => e.target.select()}
              />
              <button
                onClick={copyLink}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition ${copied ? 'bg-green-500 text-white' : 'bg-orange-500 text-white hover:bg-orange-600'}`}
                data-testid="copy-link-btn"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-3">Tip: Send this link to your doctor for a second opinion.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientAIAnalysis;
